import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from "react-redux";
import Swal from 'sweetalert2';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { FaStarOfLife } from 'react-icons/fa';
import Loader from '../components/Loader';
import { createProductAPI } from '../../services/api/ProductAPI';
import Select from "react-select";
import { getCategoriesListAPI } from '../../services/api/CategoriesAPI';

const AddProduct = () => {
    const [selectedOptions, setSelectedOptions] = useState([]);
    const user = useSelector((state) => state.LoginReducer.authData);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const token = user.data.token;
    const [options, setOptions] = useState([]);
    const [images, setImages] = useState([]);
    const [imageFile, setImageFile] = useState([]);
    
    const handleImagesChange = (e) => {
        const files = Array.from(e.target.files);
        setImageFile(prevFiles => [...prevFiles, ...files]);
        const newImages = files.map((file) => URL.createObjectURL(file));
        setImages((prevImages) => [...prevImages, ...newImages]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        setImageFile(prevFiles => [...prevFiles, ...files]);
        const newImages = files.map((file) => URL.createObjectURL(file));
        setImages((prevImages) => [...prevImages, ...newImages]);
    };

    const handleRemoveImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        setImages(newImages);
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await getCategoriesListAPI(token);
                if (data.status) {
                    const options = data.data.map(category => ({
                        value: category.id,
                        label: category.name,
                    }));
                    setOptions(options);
                }
            } catch (error) {
                console.error('Error fetching categories list:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, [token]);

    const validationSchema = Yup.object({
        name: Yup.string()
            .min(3, 'Name must be at least 3 characters long')
            .required('Name is required'),
        sku: Yup.string().required('SKU is required'),
        description: Yup.string()
            .required('Description is required'),
        short_description: Yup.string()
            .required('Short description is required'),
        regular_price: Yup.string()
            .required('Regular price is required')
            .matches(/^\d+(\.\d{1,2})?$/, 'Invalid price format (e.g., 19.99)'),  // Ensures a valid price format
        sale_price: Yup.string()
            // .required('Sale price is required')
            .matches(/^\d+(\.\d{1,2})?$/, 'Invalid price format (e.g., 14.99)'),
        tax_status: Yup.string()
            .required('Tax status is required')
            .oneOf(['taxable', 'non-taxable'], 'Invalid tax status'),
        tax_class: Yup.string()
            .required('Tax class is required'),
        stock_status: Yup.string()
            .required('Stock status is required')
            .oneOf(['In Stock', 'Out of Stock', 'On Backorder'], 'Invalid tax status'),
        stock_management: Yup.boolean()
            .nullable()
            .transform((value, originalValue) => (originalValue === "" ? false : value))
            .default(false),
        sold_individually: Yup.boolean()
            .nullable()
            .transform((value, originalValue) => (originalValue === "" ? false : value))
            .default(false),
        available_stock: Yup.number()
            .nullable()
            .default(0)
            .typeError('Stock must be a valid number'),
        categories: Yup.array()
            .required('Category is required')
            .min(1, 'At least one category is required')
            .of(Yup.number().required('Each category must be a valid category')),
        image: Yup.mixed()
            .required('Image is required')
            .test('fileType', 'Only JPEG and PNG files are allowed', (value) => {
                return value && (value.type === 'image/jpeg' || value.type === 'image/png');
            })
            .test('fileSize', 'File size exceeds the 2MB limit', (value) => {
                return value && value.size <= 2 * 1024 * 1024;
            }),
        weight: Yup.string()
            .required('Weight is required')
            .matches(/^\d+$/, 'Weight must be a valid number'),
        length: Yup.string()
            .required('Length is required')
            .matches(/^\d+$/, 'Length must be a valid number'),
        width: Yup.string()
            .required('Width is required')
            .matches(/^\d+$/, 'Width must be a valid number'),
        height: Yup.string()
            .required('Height is required')
            .matches(/^\d+$/, 'Height must be a valid number')
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
            short_description: '',
            sku: '',
            regular_price: '',
            sale_price: '',
            tax_status: '',
            tax_class: '',
            available_stock: '',
            categories: [],
            image: null,
            weight: '',
            length: '',
            width: '',
            height: '',
            stock_status: '',
            stock_management: '',
            sold_individually: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('sku', values.sku);
            formData.append('description', values.description);
            formData.append('short_description', values.short_description);
            formData.append('regular_price', values.regular_price);
            formData.append('sale_price', values.sale_price);
            formData.append('tax_status', values.tax_status);
            formData.append('tax_class', values.tax_class);
            formData.append('available_stock', values.available_stock);
            formData.append('weight', values.weight);
            formData.append('length', values.length);
            formData.append('width', values.width);
            formData.append('height', values.height);
            formData.append('stock_status', values.stock_status);
            formData.append('stock_management', values.stock_management);
            formData.append('sold_individually', values.sold_individually);
            
            values.categories.forEach(category => {
                formData.append('categories[]', category);
            });
            imageFile.forEach(image => {
                formData.append('images[]', image);
            });

            formData.append('image', values.image);
            await createProduct(formData);
        },
    });

    // Handle image change
    const handleImageChange = (event) => {
        const file = event.currentTarget.files[0];
        formik.setFieldValue('image', file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                formik.setFieldValue('imagePreview', reader.result);  // Add a preview state if necessary
            };
            reader.readAsDataURL(file);
        }
    };

    const createProduct = async (formData) => {
        try {
            const data = await createProductAPI(formData, token);
            setLoading(false);
            if (data.status) {
                Swal.fire({
                    title: 'Product Created!',
                    text: 'The Product has been created successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    navigate('/products');
                });
            }else{
                Swal.fire({
                    title: 'Error!',
                    text: 'Something went wrong please try again.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        } catch (error) {
            setLoading(false);
            Swal.fire({
                title: 'Error!',
                text: 'Something went wrong please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };
    const customStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: 'none',
            borderColor: 'none',
            boxShadow: 'none',
            borderStyle: 'none',
            '&:hover': {
                borderColor: '#007bff',
            },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#007bff' : null,
            color: state.isSelected ? 'white' : 'black',
        }),
    };
    const handleChange = (selected) => {
        setSelectedOptions(selected);
        formik.setFieldValue('categories', selectedOptions.map(option => option.value));
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Layout title="Create Product">
            <div className="container table-container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-md-6 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="name">Product name <FaStarOfLife className='text-danger' /></label>
                                    <input type="text" className="form-control mt-1" name="name" id="name" placeholder="Enter Product name" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.name} />
                                </div>
                                {formik.touched.name && formik.errors.name ? (<label className="text-danger">{formik.errors.name}</label>) : null}
                            </div>
                            <div className="col-md-6 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="sku">SKU <FaStarOfLife className='text-danger' /></label>
                                    <input type="text" className="form-control mt-1" name="sku" id="sku" placeholder="Enter Product sku" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.sku} />
                                </div>
                                {formik.touched.sku && formik.errors.sku ? (<label className="text-danger">{formik.errors.sku}</label>) : null}
                            </div>
                            <div className="col-md-12 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="categories">Product Category <FaStarOfLife className='text-danger' /></label>
                                    <Select isMulti name='categories' id='categories'
                                        value={selectedOptions}
                                        onChange={handleChange}
                                        options={options}
                                        className="form-control mt-1 p-0"
                                        classNamePrefix="select"
                                        styles={customStyles}
                                    />
                                </div>
                                {formik.touched.categories && formik.errors.categories ? (<label className="text-danger">{formik.errors.categories}</label>) : null}
                            </div>
                            <div className="col-md-12 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="short_description">Short Description <FaStarOfLife className='text-danger' /></label>
                                    <textarea className="form-control mt-1" rows={3} name="short_description" id="short_description" placeholder='Short Description' onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.short_description}>{formik.values.short_description}</textarea>
                                </div>
                                {formik.touched.short_description && formik.errors.short_description ? (<label className="text-danger">{formik.errors.short_description}</label>) : null}
                            </div>
                            <div className="col-md-12 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="description">Description <FaStarOfLife className='text-danger' /></label>
                                    <textarea className="form-control mt-1" rows={3} name="description" id="description" placeholder='Description' onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.description}>{formik.values.description}</textarea>
                                </div>
                                {formik.touched.description && formik.errors.description ? (<label className="text-danger">{formik.errors.description}</label>) : null}
                            </div>
                            <div className="col-md-3 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="regular_price">Regular Price <FaStarOfLife className='text-danger' /></label>
                                    <input type="number" className="form-control mt-1" name="regular_price" id="regular_price" placeholder="Enter Regular Price" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.regular_price} />
                                </div>
                                {formik.touched.regular_price && formik.errors.regular_price ? (<label className="text-danger">{formik.errors.regular_price}</label>) : null}
                            </div>
                            <div className="col-md-3 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="description">Sale Price <FaStarOfLife className='text-danger' /></label>
                                    <input type="number" className="form-control mt-1" name="sale_price" id="sale_price" placeholder="Enter sale price" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.sale_price} />
                                </div>
                                {formik.touched.sale_price && formik.errors.sale_price ? (<label className="text-danger">{formik.errors.sale_price}</label>) : null}
                            </div>
                            <div className="col-md-3 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="tax_status">Tax Status <FaStarOfLife className='text-danger' /></label>
                                    <select className="form-control mt-1" name='tax_status' id='tax_status' value={formik.values.tax_status} onChange={formik.handleChange} onBlur={formik.handleBlur}>
                                        <option value="">Select Option</option>
                                        <option value="taxable">Taxable</option>
                                        <option value="shipping">Shipping only</option>
                                        <option value="none">None</option>
                                    </select>
                                </div>
                                {formik.touched.tax_status && formik.errors.tax_status ? (<label className="text-danger">{formik.errors.tax_status}</label>) : null}
                            </div>
                            <div className="col-md-3 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="tax_class">Tax Class <FaStarOfLife className='text-danger' /></label>
                                    <select className="form-control mt-1" name='tax_class' id='tax_class' value={formik.values.tax_class} onChange={formik.handleChange} onBlur={formik.handleBlur}>
                                        <option value="">Select Option</option>
                                        <option value="Standard">Standard</option>
                                        <option value="Reduced rate">Reduced rate</option>
                                        <option value="Zero rate">Zero rate</option>
                                    </select>
                                </div>
                                {formik.touched.tax_class && formik.errors.tax_class ? (<label className="text-danger">{formik.errors.tax_class}</label>) : null}
                            </div>
                            
                            <div className="col-md-3 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="description">Weight <FaStarOfLife className='text-danger' /></label>
                                    <input type="number" className="form-control mt-1" name="weight" id="weight" placeholder="Enter weight" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.weight} />
                                </div>
                                {formik.touched.weight && formik.errors.weight ? (<label className="text-danger">{formik.errors.weight}</label>) : null}
                            </div>
                            <div className="col-md-3 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="length">Length <FaStarOfLife className='text-danger' /></label>
                                    <input type="number" className="form-control mt-1" name="length" id="length" placeholder="Enter length" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.length} />
                                </div>
                                {formik.touched.length && formik.errors.length ? (<label className="text-danger">{formik.errors.length}</label>) : null}
                            </div>
                            <div className="col-md-3 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="width">Width <FaStarOfLife className='text-danger' /></label>
                                    <input type="number" className="form-control mt-1" name="width" id="width" placeholder="Enter width" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.width} />
                                </div>
                                {formik.touched.width && formik.errors.width ? (<label className="text-danger">{formik.errors.width}</label>) : null}
                            </div>
                            <div className="col-md-3 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="height">Height <FaStarOfLife className='text-danger' /></label>
                                    <input type="number" className="form-control mt-1" name="height" id="height" placeholder="Enter width" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.height} />
                                </div>
                                {formik.touched.height && formik.errors.height ? (<label className="text-danger">{formik.errors.height}</label>) : null}
                            </div>
                            <div className="col-md-3 col-12">
                                <div className="form-group py-3 required">
                                    <label htmlFor="stockManagement">Stock Management</label>
                                    <div className='d-flex justify-content-start align-items-center gap-1 mt-3'>
                                        <input type="checkbox" name="stock_management" id="stock_management" checked={formik.values.stock_management} onChange={(e) => formik.setFieldValue("stock_management", e.target.checked)} onBlur={formik.handleBlur} />
                                        <label htmlFor="stock_management">Track stock quantity</label>
                                    </div>
                                </div>
                                {formik.touched.stock_management && formik.errors.stock_management ? (<label className="text-danger">{formik.errors.stock_management}</label>) : null}
                            </div>
                            <div className="col-md-3 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="description">Available Stock <FaStarOfLife className='text-danger' /></label>
                                    <input type="number" className="form-control mt-1" name="available_stock" id="available_stock" placeholder="Enter available stock" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.available_stock} />
                                </div>
                                {formik.touched.available_stock && formik.errors.available_stock ? (<label className="text-danger">{formik.errors.available_stock}</label>) : null}
                            </div>
                            <div className="col-md-3 col-12">
                                <div className="form-group py-3 required">
                                    <label htmlFor="stock_status">Stock Status <FaStarOfLife className='text-danger' /></label>
                                    <select className="form-control mt-1" name='stock_status' id='stock_status' value={formik.values.stock_status} onChange={formik.handleChange} onBlur={formik.handleBlur}>
                                        <option value="">Select Option</option>
                                        <option value="In Stock">In Stock</option>
                                        <option value="Out of Stock">Out of Stock</option>
                                        <option value="On Backorder">On Backorder</option>
                                    </select>
                                </div>
                                {formik.touched.stock_status && formik.errors.stock_status ? (<label className="text-danger">{formik.errors.stock_status}</label>) : null}
                            </div>
                            <div className="col-md-3 col-12">
                                <div className="form-group py-3 required">
                                    <label htmlFor="soldIndividually">Sold individually</label>
                                    <div className='d-flex justify-content-start align-items-center gap-1 mt-3'>
                                        <input type="checkbox" name="sold_individually" id="sold_individually" checked={formik.values.sold_individually} onChange={(e) => formik.setFieldValue("sold_individually", e.target.checked)} onBlur={formik.handleBlur} />
                                        <label htmlFor="sold_individually">Limit 1 item per order</label>
                                    </div>
                                </div>
                                {formik.touched.sold_individually && formik.errors.sold_individually ? (<label className="text-danger">{formik.errors.sold_individually}</label>) : null}
                            </div>
                            <div className="col-md-6 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="image">Product Image <FaStarOfLife className='text-danger' /></label>
                                    <input type="file" className="form-control mt-1" name="image" id="image" accept="image/*" onChange={handleImageChange} />
                                </div>
                                {formik.touched.image && formik.errors.image ? (<label className="text-danger">{formik.errors.image}</label>) : null}
                            </div>
                            {formik.values.imagePreview && (
                                <div className="col-md-6 col-12">
                                    <img className='image-preview' src={formik.values.imagePreview} alt="Image Preview" />
                                </div>
                            )}
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <div className="container table-container">
                                    <label>Upload Image</label>
                                    <div className="dropzone mt-3" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} >
                                        <p>Drag & drop your images here, or click to select files</p>
                                        <input type="file" accept="image/*" multiple onChange={handleImagesChange} style={{ display: 'none' }} id="file-input" />
                                        <label htmlFor="file-input" className="btn btn-primary">Browse Images</label>
                                    </div>
                                    <div className="row">
                                        {images.map((image, index) => (
                                            <div className="col-md-3" key={index}>
                                                <div className="my-3">
                                                    <img src={image} alt={`upload-preview-${index}`} className="img-thumbnail mb-2" />
                                                    <button className="remove-btn" onClick={() => handleRemoveImage(index)}>Remove</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row mt-3">
                            <div className="col-md-6 col-12 d-flex gap-3">
                                <Link to={'/products'} type="submit" className="btn btn-dark cancel_btn">Cancel</Link>
                                <button type="submit" className="btn btn-primary submit_btn">Submit</button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    )
}

export default AddProduct;